import React, { useState, useEffect, RefObject } from "react";
import { useNavigate, useLocation } from "react-router";
import { BsCalendar2Event } from "react-icons/bs";
import { IoIosHeartEmpty } from "react-icons/io";
import { SiEventstore } from "react-icons/si";
import { FaRegUser } from "react-icons/fa";
import { IoCalendarOutline } from "react-icons/io5";
import { MdOutlinePrivacyTip } from "react-icons/md";
import { MdOutlinePolicy } from "react-icons/md";
import { CiLogout } from "react-icons/ci";
import { LogoIcon } from "../assets/svgs";
import { appName, storageConst } from "../utils/constants";
import { getAppTheme } from "../utils/theme";
import { useUserRole } from "../hooks/useUserRole.hook";

// Define the prop types for the Sidebar component
interface SidebarProps {
  isOpen: boolean;
  toggleSidebar: () => void;
  sidebarRef: RefObject<HTMLDivElement>;
}

// Define the menu item type
interface MenuItem {
  icon: any;
  text: string;
  path: string;
  id: number;
}
const ROLE = {
  ADMIN: "ADMIN",
  USER: "USER",
};

const MENU_ITEMS = {
  [ROLE.ADMIN]: [
    { icon: BsCalendar2Event, text: "All Events", path: "/", id: 0 },
    { icon: BsCalendar2Event, text: "All Users", path: "/all-users", id: 1 },
    { icon: BsCalendar2Event, text: "Custom Ads", path: "/custom-ads", id: 2 },

    {
      icon: MdOutlinePrivacyTip,
      text: "Privacy Policy",
      path: "/privacy-policy",
      id: 3,
    },
    {
      icon: MdOutlinePolicy,
      text: "Term & Conditions",
      path: "/term-and-conditions",
      id: 4,
    },
    {
      icon: CiLogout,
      text: "Log out",
      path: "/appearance",
      id: 7,
    },
  ],
  [ROLE.USER]: [
    { icon: BsCalendar2Event, text: "All Events", path: "/", id: 0 },
    { icon: IoIosHeartEmpty, text: "Favorite", path: "/favorite", id: 1 },
    {
      icon: SiEventstore,
      text: "Attendant Events",
      path: "/attendant-events",
      id: 2,
    },
    { icon: FaRegUser, text: "Profile", path: "/profile", id: 3 },
    { icon: IoCalendarOutline, text: "Calendar", path: "/calendar", id: 4 },
    {
      icon: MdOutlinePrivacyTip,
      text: "Privacy Policy",
      path: "/privacy-policy",
      id: 5,
    },
    {
      icon: MdOutlinePolicy,
      text: "Term & Conditions",
      path: "/term-and-conditions",
      id: 6,
    },
    {
      icon: CiLogout,
      text: "Log out",
      path: "/appearance",
      id: 7,
    },
  ],
};
const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  toggleSidebar,
  sidebarRef,
}) => {
  const [activeItem, setActiveItem] = useState<number | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useUserRole(storageConst.APPTOKEN);
  useEffect(() => {
    // Dynamically set menu items based on role
    if (userRole) {
      setMenuItems(MENU_ITEMS[userRole] || []);
    }
  }, [userRole]);
  useEffect(() => {
    const currentPath = location.pathname;
    const activeIndex = menuItems.findIndex(
      (item) => item.path === currentPath
    );
    if (activeIndex !== -1) {
      setActiveItem(activeIndex);
    }
  }, [location.pathname, menuItems]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 1200);
    };

    handleResize(); // Initial check
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const handleLogout = () => {
    const isConfirmed = window.confirm("Are you sure you want to log out?");
    if (isConfirmed) {
      localStorage.clear();
      window.location.href = "/";
    }
  };
  return (
    <div
      ref={sidebarRef}
      className={`bg-bgCard shadow  px-3 ${
        !isOpen && isSmallScreen ? "w-16" : "w-64"
      } space-y-6 absolute inset-y-0 left-0 transform ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } md:relative md:translate-x-0 transition duration-200 ease-in-out z-20 flex flex-col justify-between max-h-screen overflow-y-auto md:overflow-auto`}
    >
      <div>
        <div
          className={`flex items-center my-5 justify-between  ${
            isSmallScreen ? "px-1" : "pl-3"
          }`}
        >
          {!isOpen && isSmallScreen ? (
            <LogoIcon width="40px" height="40px" />
          ) : (
            <div className="flex items-center gap-x-2">
              <div className="">
                <LogoIcon width="30px" height="30px" />
              </div>
              <div className="text-base font-semibold">{appName}</div>
            </div>
          )}
        </div>

        <nav>
          {menuItems.map((item, index) => (
            <div
              key={item.id}
              className={`h-[46px] rounded-[50px] text-base flex items-center hover:text-white hover:bg-primary  
        ${!isOpen && isSmallScreen ? "px-2 justify-center" : "px-4"}
        cursor-pointer transition duration-200 my-1
        ${activeItem === index ? "bg-primary text-white rounded-[50px]" : ""}`}
              onClick={() => {
                if (item.text === "Log out") {
                  handleLogout(); // Call logout function only for Log out
                } else {
                  setActiveItem(index);
                  navigate(item.path); // Navigate for other menu items
                }
              }}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
              title={isSmallScreen ? item.text : ""}
            >
              <div className="text-lg">
                <item.icon
                  isActive={
                    activeItem === index ||
                    hoveredItem === index ||
                    getAppTheme() === "dark"
                  }
                />
              </div>
              <div
                className={`flex justify-between items-center w-full ${
                  !isOpen && isSmallScreen ? "hidden" : ""
                }`}
              >
                <div className="mx-3">{item.text}</div>
              </div>
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default Sidebar;
